// Zustand exam-session store. Persisted to sessionStorage so a mid-exam
// connection drop / reload restores the full session.
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Attempt, Paper, Question, QuestionResult, SupportPayload } from '@/types';

/**
 * A wrong-but-open grade delivered over SSE, waiting for the AnswerPanel to apply it.
 *
 * It cannot be applied here: the panel holds the submitted answer text, which the
 * event does not carry, and the panel owns the support reveal. So the stream drops
 * it in a mailbox and the panel picks it up on its next render.
 */
export interface OpenLadderGrade {
  questionId: string;
  attemptIndex: number;
  support: SupportPayload | null;
}

/** Local state machine for the AnswerPanel (the attempt ladder). */
export type AnswerPhase =
  | 'IDLE'
  | 'ANSWERING'
  | 'GRADING'
  | 'RESULT'
  | 'ATTEMPT_2'
  | 'ATTEMPT_3'
  | 'DONE';

export interface ExamStore {
  spId: string | null;
  paper: Paper | null;
  questions: Question[];
  attempts: Record<string, Attempt[]>; // keyed by questionId
  results: Record<string, QuestionResult>; // keyed by questionId
  completionOrder: string[]; // questionIds in the order they were finalized
  activeQuestionId: string | null;
  elapsedSeconds: number;
  effortTracking: { hintSeconds: number; videoSeconds: number; theorySeconds: number };
  panelPhase: AnswerPhase;
  pendingJobId: string | null;
  /** Transient — deliberately not persisted; a reload re-reads state from the server. */
  openLadderGrade: OpenLadderGrade | null;

  // hydration / lifecycle
  initSession: (payload: { spId: string; paper: Paper; questions: Question[] }) => void;
  hydrateServerState: (attempts: Attempt[], results: QuestionResult[]) => void;
  reset: () => void;

  // actions
  setActiveQuestion: (id: string) => void;
  recordAttempt: (attempt: Attempt) => void;
  recordResult: (result: QuestionResult) => void;
  incrementEffort: (type: 'hint' | 'video' | 'theory', delta: number) => void;
  tick: () => void;
  setPanelPhase: (phase: AnswerPhase) => void;
  setPendingJob: (jobId: string | null) => void;
  setOpenLadderGrade: (grade: OpenLadderGrade | null) => void;
}

const EFFORT_KEY: Record<'hint' | 'video' | 'theory', keyof ExamStore['effortTracking']> = {
  hint: 'hintSeconds',
  video: 'videoSeconds',
  theory: 'theorySeconds',
};

const initialData = {
  spId: null,
  paper: null,
  questions: [] as Question[],
  attempts: {} as Record<string, Attempt[]>,
  results: {} as Record<string, QuestionResult>,
  completionOrder: [] as string[],
  activeQuestionId: null,
  elapsedSeconds: 0,
  effortTracking: { hintSeconds: 0, videoSeconds: 0, theorySeconds: 0 },
  panelPhase: 'IDLE' as AnswerPhase,
  pendingJobId: null,
  openLadderGrade: null as OpenLadderGrade | null,
};

export const useExamStore = create<ExamStore>()(
  persist(
    (set) => ({
      ...initialData,

      initSession: ({ spId, paper, questions }) =>
        set((s) => {
          // Resuming the same session keeps prior progress; a new one resets.
          if (s.spId === spId) return { paper, questions };
          return { ...initialData, spId, paper, questions };
        }),

      hydrateServerState: (attempts, results) =>
        set(() => {
          const byQ: Record<string, Attempt[]> = {};
          for (const a of attempts) {
            (byQ[a.questionId] ??= []).push(a);
          }
          for (const list of Object.values(byQ)) {
            list.sort((x, y) => x.attemptIndex - y.attemptIndex);
          }
          const resMap: Record<string, QuestionResult> = {};
          const order: string[] = [];
          for (const r of results) {
            resMap[r.questionId] = r;
            order.push(r.questionId);
          }
          return { attempts: byQ, results: resMap, completionOrder: order };
        }),

      reset: () => set(() => ({ ...initialData })),

      setActiveQuestion: (id) => set(() => ({ activeQuestionId: id, panelPhase: 'ANSWERING' })),

      recordAttempt: (attempt) =>
        set((s) => {
          const existing = s.attempts[attempt.questionId] ?? [];
          // Upsert by attemptIndex (idempotent on retry / socket echo).
          const next = existing.filter((a) => a.attemptIndex !== attempt.attemptIndex);
          next.push(attempt);
          next.sort((a, b) => a.attemptIndex - b.attemptIndex);
          return { attempts: { ...s.attempts, [attempt.questionId]: next } };
        }),

      recordResult: (result) =>
        set((s) => {
          const alreadyDone = Boolean(s.results[result.questionId]);
          return {
            results: { ...s.results, [result.questionId]: result },
            completionOrder: alreadyDone
              ? s.completionOrder
              : [...s.completionOrder, result.questionId],
            // If this is the active question, surface the reveal.
            panelPhase: s.activeQuestionId === result.questionId ? 'RESULT' : s.panelPhase,
          };
        }),

      incrementEffort: (type, delta) =>
        set((s) => {
          const key = EFFORT_KEY[type];
          return { effortTracking: { ...s.effortTracking, [key]: s.effortTracking[key] + delta } };
        }),

      tick: () => set((s) => ({ elapsedSeconds: s.elapsedSeconds + 1 })),

      setPanelPhase: (phase) => set(() => ({ panelPhase: phase })),

      setPendingJob: (jobId) => set(() => ({ pendingJobId: jobId })),

      setOpenLadderGrade: (grade) => set(() => ({ openLadderGrade: grade })),
    }),
    {
      name: 'cwy-exam-session',
      storage: createJSONStorage(() => sessionStorage),
      // Persist only data, not action fns.
      partialize: (s) => ({
        spId: s.spId,
        paper: s.paper,
        questions: s.questions,
        attempts: s.attempts,
        results: s.results,
        completionOrder: s.completionOrder,
        activeQuestionId: s.activeQuestionId,
        elapsedSeconds: s.elapsedSeconds,
        effortTracking: s.effortTracking,
        panelPhase: s.panelPhase,
        pendingJobId: s.pendingJobId,
      }),
    },
  ),
);

// ── Derived selectors (kept outside the store to avoid re-renders) ──

export function attemptsFor(state: ExamStore, questionId: string): Attempt[] {
  return state.attempts[questionId] ?? [];
}

/** Next attempt index (1..3) for a question, or null if finalized. */
export function nextAttemptIndex(state: ExamStore, questionId: string): number | null {
  if (state.results[questionId]) return null;
  const used = attemptsFor(state, questionId).filter((a) => a.isCorrect !== null).length;
  return Math.min(used + 1, 3);
}

export function attemptedCount(state: ExamStore): number {
  const ids = new Set<string>(Object.keys(state.results));
  for (const [qid, list] of Object.entries(state.attempts)) {
    if (list.length > 0) ids.add(qid);
  }
  return ids.size;
}
